import { RequestedHistory } from './../history';
import { DatePipe } from '@angular/common';
import { OnInit, Component, ViewChild, forwardRef, Input } from '@angular/core';
import { RequestedHistoryRequest, SigningHistory } from '../history';
import { InfoService } from '@app/core/info/info.service';
import { HistoryService } from '../history.service';
import { MatTableHelper } from '@app/shared/helpers/table-filter.helper';
import { SigningService } from '@app/certificates/signing/signing.service';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { getCertificateFullName } from '@app/shared/helpers/certificate-name-helper';
import { Subject } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@env/environment';
import { ICertificateData } from '@app/certificates/manage/manage';
import { selectorCertificate } from '@app/certificates/manage/manage.reducer';
import { takeUntil } from 'rxjs/operators';
import { IStatusIcon } from '@app/shared/interfaces/common-interfaces';
import { iconCodes } from '@app/shared/cqes-icon/cqes-icon.component';
import { CertificateDetailsComponent } from '@app/certificates/manage/certificate-details/certificate-details.component';
import { RequestedHistoryDetailsComponent } from './requested-history-details/requested-history-details.component';
import { IUserData } from '@app/profile/main/details';
import { SessionStorageService, selectorAuth } from '@app/core';

export enum DocumentStatus {
  ERROR = 'ERROR',
  IN_PROGRESS = 'IN_PROGRESS',
  SIGNED = 'SIGNED',
  RECEIVED = 'RECEIVED',
  REJECTED = 'REJECTED',
  ARCHIVED = 'ARCHIVED',
  REMOVED = 'REMOVED',
  EXPIRED = 'EXPIRED'
}

export enum SortableClolumsNames {
  SERIAL_NUMBER = 'certSerialNumber',
  TEXT_TO_SHOW = 'displayedText',
  DATE = 'createdDate',
  RELYING_PARTY = 'relyingPartyName',
  SIGNED_DATE = 'signedDate',
  DOWNLOAD_TS = 'downloadeTimestamp'
}

export enum OrderHistorySortColumns {
  ORDER_DATE = 'order_date',
  ORDER_CODE = 'order_code',
  PLAN_NAME = 'plan_id',
  CERTIFICATE_SERIAL_NUMBER = 'serial_number',
  ORDER_STATUS = 'status'
}

export const DocumentStatusMap = new Map<string, IStatusIcon>();
DocumentStatusMap.set(DocumentStatus.ERROR, {
  icon: iconCodes.error,
  colorClass: 'text-danger',
  tooltip: 'historyTexts.documentStatusError',
  key: DocumentStatus.ERROR,
  text: 'error'
} as IStatusIcon);
DocumentStatusMap.set(DocumentStatus.IN_PROGRESS, {
  icon: iconCodes.info,
  colorClass: 'text-warning',
  tooltip: 'historyTexts.documentStatusSigning',
  key: DocumentStatus.IN_PROGRESS,
  text: 'in progress'
} as IStatusIcon);
DocumentStatusMap.set(DocumentStatus.SIGNED, {
  icon: iconCodes.done,
  colorClass: 'text-success',
  tooltip: 'historyTexts.documentStatusSigned',
  key: DocumentStatus.SIGNED,
  text: 'successfully signed'
} as IStatusIcon);
DocumentStatusMap.set(DocumentStatus.RECEIVED, {
  icon: iconCodes.archive,
  colorClass: 'text-success',
  tooltip: 'historyTexts.documentStatusReceived',
  key: DocumentStatus.RECEIVED,
  text: 'received'
} as IStatusIcon);
DocumentStatusMap.set(DocumentStatus.REJECTED, {
  icon: iconCodes.blocked,
  colorClass: 'mat-primary',
  tooltip: 'historyTexts.documentStatusRejected',
  key: DocumentStatus.REJECTED,
  text: 'rejected by owner'
} as IStatusIcon);
DocumentStatusMap.set(DocumentStatus.ARCHIVED, {
  icon: iconCodes.folder,
  colorClass: 'text-success',
  tooltip: 'historyTexts.documentStatusArchived',
  key: DocumentStatus.ARCHIVED,
  text: 'archived'
} as IStatusIcon);
DocumentStatusMap.set(DocumentStatus.REMOVED, {
  icon: iconCodes.delete,
  colorClass: 'text-danger',
  tooltip: 'historyTexts.documentStatusDeleted',
  key: DocumentStatus.REMOVED,
  text: 'removed'
} as IStatusIcon);
DocumentStatusMap.set(DocumentStatus.EXPIRED, {
  icon: iconCodes.hourglass_empty,
  colorClass: 'mat-primary',
  tooltip: 'historyTexts.documentStatusExpired',
  key: DocumentStatus.EXPIRED,
  text: 'expired'
} as IStatusIcon);

@Component({
  selector: 'cloud-requested-history',
  templateUrl: './requested-history.component.html',
  styleUrls: ['./requested-history.component.scss']
})

export class RequestedHistoryComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(forwardRef(() => MatSort)) sort: MatSort;
  @Input() isSubcomponent = false;
  unsubscribe$: Subject<void> = new Subject<void>();
  requestedHistoryDataSource: MatTableDataSource<RequestedHistory> = new MatTableDataSource<RequestedHistory>();

  readonly displayedColumns = [
    'status',
    'signCountTxt',
    'date',
    'downloadeTimestamp',
    'documentName',
    'downloadDocument'
  ];

  statusLables = [
    'ALL',
    'ERROR',
    'IN_PROGRESS',
    'SIGNED',
    'RECEIVED',
    'REJECTED',
    'ARCHIVED',
    'REMOVED',
    'EXPIRED'
  ];

  displayOptions: RequestedHistoryRequest;
  readonly dateFormat = environment.dateFormat;
  readonly timeFormat = environment.timeFormat;
  certificateNumbers: string[];
  displayAllCertsOption = false;
  certificates: ICertificateData[];
  fromDate: string;
  toDate: string;
  minEndDateValue: string;
  maxStartDateValue: string;
  documentNumber: any;
  isLoading = true;
  signedfileName: any;
  resetCertificate: string;
  userData: IUserData;

  constructor(
    public info: InfoService,
    private historyService: HistoryService,
    private signingService: SigningService,
    public tableHelper: MatTableHelper,
    private translateService: TranslateService,
    private store: Store<any>,
    public dialog: MatDialog,
    private sessionStorageService: SessionStorageService
  ) {
    this.store
      .select(selectorCertificate)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(certs => this.certificates = certs);
  }

  ngOnInit() {
    this.initDisplayOptions();
    
    this.certificateNumbers = [];
    this.certificates.forEach(cert => {
      this.certificateNumbers.push(cert.serialNo);
    });
    this.getUserInfo();
    this.loadRequestedHistory();
  }

  getUserInfo() {
    this.store
      .select(selectorAuth)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        if (res.userData) { this.userData = res.userData; }
        if(this.userData == undefined){          
          this.userData =  this.sessionStorageService.getItem("loginEventData")
        }  
      });
  }

  getDocumentStatusText(value) {
    switch (value) {
      case 'ALL':
        return this.translateService.instant('historyTexts.all');
      case 'ERROR':
        return this.translateService.instant('historyTexts.documentStatusError');
      case 'IN_PROGRESS':
        return this.translateService.instant('historyTexts.documentStatusSigning');
      case 'SIGNED':
        return this.translateService.instant('historyTexts.documentStatusSigned');
      case 'RECEIVED':
        return this.translateService.instant('historyTexts.documentStatusReceived');
      case 'REJECTED':
        return this.translateService.instant('historyTexts.documentStatusRejected');
      case 'ARCHIVED':
        return this.translateService.instant('historyTexts.documentStatusArchived');
      case 'REMOVED':
        return this.translateService.instant('historyTexts.documentStatusDeleted');
      case 'EXPIRED':
        return this.translateService.instant('historyTexts.documentStatusExpired');
      default:
        return this.translateService.instant('historyTexts.documentStatusSigned');
    }
  }

  private initDisplayOptions() {
    this.displayOptions = {};
    this.displayOptions.offset = 0;
    this.displayOptions.limit = 10;
  }

  loadRequestedHistory() {
    this.historyService.getRequestsHistory(this.displayOptions, false).subscribe(res => {
      if (res.data) {
        this.setDataSource(res.data.content);
        this.documentNumber = res.data.size;
        this.setPaginator();
      }
    });
  }

  // tslint:disable-next-line:no-shadowed-variable
  setDataSource(requestedHistory: RequestedHistory[]) {
    this.isLoading = false;
    let dataSource = [];

    if (requestedHistory) {
        dataSource = requestedHistory.map(a => Object.assign({
          date: a.requestedDate,
          downloadeTimestamp: a.requestedDownloadDate,
          documentName: a.fileName,
          relyingPartyId: a.rpId,
          relyingPartyName: a.rpName,
          status: a.status,
          statusIcon: DocumentStatusMap.get(a.status),
          fileId: a.fileId,
          signCountTxt: a.signCount + '/' + a.reqSignCount,
          signCount: a.signCount,
          reqSignCount: a.reqSignCount,
          requestedId: a.requestedId
        }));
    }

    if (this.certificateNumbers.length > 1) {
        this.displayAllCertsOption = true;
      }
      this.requestedHistoryDataSource = new MatTableDataSource(dataSource);
    }

  getCert(certSerialNumber) {
    return this.certificates.find(c => c.serialNo === certSerialNumber);
  }

  hasData() {
    return (this.requestedHistoryDataSource?.data?.length !== 0);
  }

  setOrderStatus() {
    if (this.displayOptions.status && this.displayOptions.status === 'ALL') {
      delete this.displayOptions.status;
    }
    this.displayOptions.offset = 0;
    this.loadRequestedHistory();
  }

  private setPaginator() {
    this.paginator.pageIndex = this.displayOptions.offset;
    this.paginator.pageSize = this.displayOptions.limit;
    this.paginator.length = this.documentNumber;
  }

  setRequestDates() {
    const pip: DatePipe = new DatePipe('bg-BG');

    if (this.fromDate) {
      this.minEndDateValue = this.fromDate;
      const startDate = new Date(this.fromDate).setHours(0, 0, 0, 0);
      this.displayOptions.fromCreatedDate = encodeURIComponent(pip.transform(startDate, 'yyyy-MM-dd\'T\'HH:mm:ss.SSSZ'));
    }

    if (this.toDate) {
      this.maxStartDateValue = this.toDate;
      const endDate = new Date(this.toDate).setHours(23, 59, 59, 999);
      this.displayOptions.toCreatedDate = encodeURIComponent(pip.transform(endDate, 'yyyy-MM-dd\'T\'HH:mm:ss.SSSZ'));
    }
    this.displayOptions.offset = 0;
    this.loadRequestedHistory();
  }

  searchRequestedDocument() {
    if (this.displayOptions.textToShow) {
      this.displayOptions.offset = 0;
      this.loadRequestedHistory();
    }
  }

  clearText() {
    this.displayOptions.textToShow = '';
    delete this.displayOptions.textToShow;
    this.displayOptions.offset = 0;
    this.loadRequestedHistory();
  }

  resetFilters() {
    this.fromDate = '';
    this.maxStartDateValue = '';
    this.toDate = '';
    this.minEndDateValue = '';
    this.resetCertificate = '';
    this.initDisplayOptions();
    this.loadRequestedHistory();
  }

  sortByHeader(sorter) {
    const columnName = this.getColumnName(sorter.id);
    if (!sorter._arrowDirection) {
      sorter._arrowDirection = 'desc';
    }
    this.displayOptions.orderByFilter = columnName;
    this.displayOptions.order = sorter._arrowDirection.toUpperCase();
    this.loadRequestedHistory();
  }


  getColumnName(name):string {
    switch (name) {
      case 'date':
        return SortableClolumsNames.DATE;
      case 'documentName':
        return SortableClolumsNames.TEXT_TO_SHOW;
      case 'certSerialNumber':
        return SortableClolumsNames.SERIAL_NUMBER;
      case 'relyingPartyName':
        return SortableClolumsNames.RELYING_PARTY;
      case 'signedDate':
        return SortableClolumsNames.SIGNED_DATE;
      case 'downloadeTimestamp':
          return SortableClolumsNames.DOWNLOAD_TS;
      default:
        return SortableClolumsNames.DATE;
    }
  }

  checkForDownload(cert: SigningHistory): boolean {
    return cert.fileId !== '-1';
  }

  hasDownloadFileRequested(req: RequestedHistory): boolean {
    const hasFile = req.fileId != '-1';
    return hasFile;
  }

  isArhived(cert: SigningHistory): boolean {
    return cert.status === DocumentStatus.ARCHIVED;
  }

  onDownloadCert(cert: SigningHistory) {
    this.signingService.getRequestedFile(cert.fileId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (result) => {
          if (result.status !== 200) {
            this.info.error(result.message);
          } else {
            this.signedfileName = result.headers.get('Content-Disposition');
            let fileName = this.signedfileName.substring(this.signedfileName.indexOf('=') + 2, (this.signedfileName.length - 1));
            if (this.isIE()) {
              fileName = decodeURIComponent(fileName);
              const nav = (window.navigator as any) 
              if(nav.msSaveOrOpenBlob){
                nav.msSaveOrOpenBlob(result.body, fileName);
              }
            } else {
              const url = window.URL.createObjectURL(result.body);
              const link = document.createElement('a');
              link.href = url;
              link.download = decodeURI(fileName);
              document.body.appendChild(link);
              link.click();
            }
          }
        }
      );
  }

  isIE() {
    const match = navigator.userAgent.search(/(?:Edge|MSIE|Trident\/.*; rv:)/);
    let isIE = false;
    if (match !== -1) {
      isIE = true;
    }
    return isIE;
  }

  onDownloadReport(cert: SigningHistory, reportType: string) {
    this.signingService.getReport(cert.fileId, reportType)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (result) => {
          if (result.status !== 200) {
            this.info.error(result.message);
          } else {
            this.signedfileName = result.headers.get('Content-Disposition');
            let fileName = this.signedfileName.substring(this.signedfileName.indexOf('=') + 2, (this.signedfileName.length - 1));
            if (this.isIE()) {
              fileName = decodeURIComponent(fileName);
              const nav = (window.navigator as any) 
              if(nav.msSaveOrOpenBlob){
                nav.msSaveOrOpenBlob(result.body, fileName);
              }
            } else {
              const url = window.URL.createObjectURL(result.body);
              const link = document.createElement('a');
              link.href = url;
              link.download = decodeURI(fileName);
              document.body.appendChild(link);
              link.click();
            }
          }
        }
      );
  }

  onPaginateChange(paginator) {
    this.displayOptions.limit = paginator.pageSize;
    this.displayOptions.offset += (paginator.pageIndex - paginator.previousPageIndex);
    this.loadRequestedHistory();
  }

  openRequestedDetailsDialog(requestedId: number) {
    const dialogRef = this.dialog.open(RequestedHistoryDetailsComponent, { data: requestedId });
    const sub = dialogRef.componentInstance.close.subscribe(() => {
      this.dialog.closeAll();
    });
  }
}
